import { useState, useEffect, useRef } from "react";
import { Command, SendHorizontal, Bot, X, Sparkles, Clock, History } from "lucide-react";
import GeminiAnalysis from "./GeminiAnalysis";

const SaarthiAI = ({ isOpen, setIsOpen }) => {
  const [activeQuery, setActiveQuery] = useState(null);
  const inputRef = useRef(null);

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'j' && (e.metaKey || e.ctrlKey)) {
        e.preventDefault();
        setIsOpen(true);
      } else if (e.key === 'Escape') {
        setIsOpen(false);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [setIsOpen]);

  // Auto-focus input when menu opens
  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isOpen]);

  const quickPrompts = [
    {
      icon: Bot,
      title: "Tell me about Saarthi",
      prompt: "Give me a comprehensive overview of Saarthi Alumni Platform and its key features.",
      type: "info"
    },
    {
      icon: Sparkles,
      title: "Career Guidance",
      prompt: "As an AI career advisor for Saarthi platform, provide personalized career guidance considering the alumni network and opportunities available.",
      type: "guidance"
    },
    {
      icon: History,
      title: "Alumni Success Stories",
      prompt: "Share insights about alumni success stories and how the platform facilitates alumni growth.",
      type: "stories"
    }
  ];

  const navigationSuggestions = [
    { label: "Alumni Directory", path: "/directory", description: "Browse and connect with fellow alumni" },
    { label: "Job Portal", path: "/jobs", description: "Explore job opportunities and referrals" },
    { label: "Events", path: "/events", description: "Upcoming alumni events and reunions" },
    { label: "Campaigns", path: "/campaigns", description: "Support alumni initiatives and donations" },
    { label: "My Profile", path: "/profile", description: "View and edit your profile" },
    { label: "Chat", path: "/chat", description: "Connect with fellow alumni" }
  ];

  if (!isOpen) return null;

  if (activeQuery) {
    return (
      <GeminiAnalysis
        onClose={() => {
          setActiveQuery(null);
          setIsOpen(false);
        }}
        title="Saarthi AI Assistant"
        initialPrompt={activeQuery}
        data={{
          platform: {
            name: "Saarthi",
            type: "Alumni Association Platform",
            features: [
              "Alumni Directory with Map Integration",
              "Job Portal with Referral System",
              "Events and Reunions",
              "Donation Campaigns",
              "Chat System",
              "AI-Powered Analysis"
            ]
          }
        }}
        placeholder="Ask follow-up questions..."
      />
    );
  }

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="min-h-screen px-4 text-center">
        <div 
          className="fixed inset-0 bg-gray-900/50 backdrop-blur-sm transition-opacity" 
          onClick={() => setIsOpen(false)} 
        />

        <div className="inline-block w-full max-w-2xl my-16 overflow-hidden text-left align-middle transition-all transform">
          <div className="relative bg-white rounded-2xl shadow-2xl ring-1 ring-gray-200">
            {/* Header */}
            <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200">
              <div className="flex items-center space-x-2">
                <Bot className="w-5 h-5 text-blue-600" />
                <h2 className="text-lg font-semibold text-gray-900">Saarthi AI Assistant</h2>
              </div>
              <button
                onClick={() => setIsOpen(false)}
                className="text-gray-400 hover:text-gray-500"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Search Input */}
            <div className="px-4 py-3 border-b border-gray-200">
              <div className="flex items-center space-x-3">
                <Command className="w-5 h-5 text-gray-400" />
                <input
                  ref={inputRef}
                  type="text"
                  placeholder="Ask anything about Saarthi..."
                  className="flex-1 bg-transparent border-0 focus:ring-0 text-gray-900 
                    placeholder-gray-400 text-sm outline-none"
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && e.target.value) {
                      setActiveQuery(e.target.value);
                    }
                  }}
                />
                <kbd className="hidden sm:inline-flex items-center px-2 py-1.5 text-xs font-semibold 
                  text-gray-500 bg-gray-100 rounded-md border border-gray-300">
                  ⌘ J
                </kbd>
              </div>
            </div>

            {/* Quick Prompts */}
            <div className="p-4 border-b border-gray-200">
              <h3 className="text-xs font-semibold text-gray-500 mb-3">QUICK PROMPTS</h3>
              <div className="grid gap-2">
                {quickPrompts.map((item) => (
                  <button
                    key={item.title}
                    className="flex items-center space-x-3 p-3 w-full text-left rounded-lg
                      hover:bg-gray-50 transition-colors"
                    onClick={() => setActiveQuery(item.prompt)}
                  >
                    <item.icon className="w-5 h-5 text-blue-600" />
                    <span className="text-sm font-medium text-gray-700">{item.title}</span>
                  </button>
                ))}
              </div>
            </div>

            {/* Navigation Shortcuts */}
            <div className="p-4">
              <h3 className="text-xs font-semibold text-gray-500 mb-3">NAVIGATION</h3>
              <div className="grid gap-2">
                {navigationSuggestions.map((item) => (
                  <a
                    key={item.path}
                    href={item.path}
                    className="flex items-center justify-between p-3 rounded-lg
                      hover:bg-gray-50 transition-colors"
                  >
                    <div>
                      <div className="text-sm font-medium text-gray-700">{item.label}</div>
                      <div className="text-xs text-gray-500">{item.description}</div>
                    </div>
                    <SendHorizontal className="w-4 h-4 text-gray-400" />
                  </a>
                ))}
              </div>
            </div>

            {/* Footer */}
            <div className="px-4 py-3 bg-gray-50 border-t border-gray-200">
              <div className="flex items-center justify-between text-xs text-gray-500">
                <span>Press Enter ↵ to send your query</span>
                <div className="flex items-center">
                  <Command className="w-3 h-3 mr-1" />
                  <span>+ J to open</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SaarthiAI;